import { pips as pipsText, ARMOR_SLOTS, CHEST_SLOTS, itemDef, type ItemDef, type ProcessStation } from "@eldrun/content";
import type { InventoryType, ItemStackType } from "@eldrun/protocol";
import { BELT_SLOTS, INVENTORY_SLOTS, type SlotRef } from "@eldrun/sim";
import type { InventoryActions } from "../../net/inventory-actions.ts";
import type { ItemIcons } from "../../render/item-icons.ts";
import { badge, bar, button, divider, icon, panel, pips, setBar, statRow } from "../kit/components.ts";
import { h } from "../kit/dom.ts";
import { CRAFT_CATEGORIES, inCategory, type CraftCategory } from "./crafting-model.ts";
import { ARMOR_LABELS, capacity, itemDetail, slotActions, slotName, type OpenStorage, type SlotActionId } from "./inventory-model.ts";
import { StationClock, stationBook, stationLabels, stationTitle, type StationLike } from "./station-model.ts";

/** Slots do maior armazenamento aberto: o baú (18); Forno e Panela usam os primeiros. */
const STATION_MAX_SLOTS = CHEST_SLOTS;
/** `StationState.kind` dos baús. */
const CHEST_KIND = "bau";
/** Nível máximo dos itens (os pips do detalhe: ◆◆◇◇◇). */
const MAX_TIER = 5;
const EMPTY = { itemId: "", qty: 0 };
/** O atalho de cada botão do detalhe (a dica do botão). */
const ACTION_SHORTCUTS: Record<SlotActionId, string> = {
  transferir: "Transferir (Shift+clique)",
  dividir: "Dividir ao meio (botão direito)",
  segurar: "Pôr na mão (teclas 1–8)",
  largar: "Largar a pilha no chão (arrastar para fora)",
};

/** Descrição curta do item (tooltip). */
export function describeItem(def: ItemDef | undefined): string {
  if (!def) return "";
  const lines = [def.name];
  if (def.tool) lines.push(`Coleta ${def.tool.damage} · nível ${def.tool.tier}`);
  if (def.weapon && def.weapon.family !== "arco") lines.push(`Dano ${def.weapon.damage} · velocidade ${def.weapon.speed}`);
  if (def.weapon?.family === "arco") lines.push(`Arco ×${def.weapon.damage} · carga ${def.weapon.charge} s`);
  if (def.ammo) lines.push(`Flecha · dano ${def.ammo.damage}`);
  if (def.armor) lines.push(`Defesa ${def.armor.defense} · ${ARMOR_LABELS[def.armor.slot]}`);
  if (def.food && !def.food.inedible) lines.push(`Vida +${def.food.hp} · Fome +${def.food.hunger} · Fôlego +${def.food.stamina}`);
  return lines.join("\n");
}

const refKey = (r: SlotRef) => `${r.area}:${r.index}`;

interface DetailView {
  box: HTMLElement;
  icon: HTMLElement;
  name: HTMLElement;
  type: HTMLElement;
  badges: HTMLElement;
  hint: HTMLElement;
  stats: HTMLElement;
  buttons: Map<SlotActionId, HTMLButtonElement>;
}

/**
 * Inventário (Tab/I) no kit da F79, como o mockup ui-c: categorias à esquerda (filtram pelo tipo do item,
 * as mesmas da fabricação), a grade (cinto, mochila, armadura e Aljava; a estação ou o baú aberto em cima),
 * o detalhe do item à direita (passar o mouse mostra; clicar ou focar escolhe) com os botões de ação, e a
 * ocupação embaixo. Arrastar move a pilha (o servidor decide), soltar fora do painel descarta, botão
 * direito divide, Shift+clique transfere rápido, duplo clique junta pilhas.
 */
export class InventoryPanel {
  readonly element: HTMLElement;
  private readonly actions: InventoryActions;
  private readonly icons: ItemIcons;
  private readonly slots = new Map<string, HTMLElement>();
  /** A última pilha pintada em cada slot (o detalhe e o filtro leem daqui). */
  private readonly stacks = new Map<string, { itemId: string; qty: number }>();
  private drag: { from: SlotRef; ghost: HTMLElement } | undefined;
  private lastKey = "";

  private category: CraftCategory = "todos";
  private readonly categoryButtons = new Map<CraftCategory, HTMLButtonElement>();
  /** Slot escolhido (clique ou foco) e o que está sob o mouse; sem nenhum, o detalhe mostra o item na mão. */
  private inspected: SlotRef | undefined;
  private hovered: SlotRef | undefined;
  private held = 0;
  private detailKey = "";
  private readonly detail: DetailView;
  private readonly itemsBar: HTMLElement;
  private readonly coins: HTMLElement;

  private readonly stationBox: HTMLElement;
  private readonly stationTitle: HTMLElement;
  private readonly fireFill: HTMLElement;
  private readonly progressFill: HTMLElement;
  private readonly bars: HTMLElement[];
  private readonly book: HTMLElement;
  private readonly stationClock = new StationClock();
  private stationKey = "";
  private stationOpenedAt = -1;

  /** `onClose`: o ✕ do painel (a cena fecha junto a estação aberta); sem ele, só esconde. */
  constructor(parent: HTMLElement, actions: InventoryActions, icons: ItemIcons, onClose?: () => void) {
    this.actions = actions;
    this.icons = icons;
    const grid = (label: string, refs: SlotRef[], labels?: string[]) =>
      h("div", { class: "inv-group" }, h("h3", {}, label), h("div", { class: "inv-grid" }, ...refs.map((ref, i) => this.slotElement(ref, labels?.[i]))));
    // Estação aberta (Forno, Panela) ou baú: os slots dela entram no mesmo painel, com o mesmo
    // arraste, Shift+clique e divisão; os que a estação não usa ficam escondidos.
    this.stationTitle = h("h3");
    const stationRow = h("div", { class: "inv-grid" });
    for (let i = 0; i < STATION_MAX_SLOTS; i++) stationRow.appendChild(this.slotElement({ area: "estacao", index: i }));
    const stationBar = (cls: string) => {
      const fill = h("div", { class: "fill" });
      return { outer: h("div", { class: `barra ${cls}` }, fill), fill };
    };
    const fire = stationBar("fogo");
    const progress = stationBar("progresso");
    this.fireFill = fire.fill;
    this.progressFill = progress.fill;
    this.bars = [fire.outer, progress.outer];
    this.book = h("ul", { class: "livro" });
    this.stationBox = h("div", { class: "inv-group estacao" }, this.stationTitle, stationRow, fire.outer, progress.outer, this.book);
    this.stationBox.hidden = true;

    const belt = Array.from({ length: BELT_SLOTS }, (_, i) => ({ area: "inv" as const, index: i }));
    const bag = Array.from({ length: INVENTORY_SLOTS - BELT_SLOTS }, (_, i) => ({ area: "inv" as const, index: BELT_SLOTS + i }));
    const armor = ARMOR_SLOTS.map((_, i) => ({ area: "armor" as const, index: i }));

    // Categorias à esquerda (ui-c): as da fabricação, filtrando a grade pelo tipo do item.
    const categories = h(
      "div",
      { class: "cat-coluna", role: "toolbar", "aria-label": "Filtrar por categoria", "aria-orientation": "vertical" },
      ...CRAFT_CATEGORIES.map((c) => {
        const b = h("button", { type: "button", class: "cat-botao", "data-categoria": c.id, title: c.name, "aria-label": c.name, onclick: () => this.setCategory(c.id) }, icon(`categoria-${c.id}`));
        this.categoryButtons.set(c.id, b);
        return b;
      }),
    );

    this.itemsBar = bar({ label: "Itens", icon: "icone-itens", value: 0, max: INVENTORY_SLOTS });
    this.coins = h("span", { class: "k-linha-valor" }, "0");
    const footer = h("div", { class: "inv-rodape" }, this.itemsBar, h("div", { class: "inv-moedas" }, icon("icone-moedas"), h("span", { class: "k-linha-nome" }, "Moedas"), this.coins));

    this.detail = this.buildDetail();
    const center = h(
      "div",
      { class: "inv-centro" },
      this.stationBox,
      grid("Cinto (1–8)", belt),
      grid("Mochila", bag),
      grid("Armadura e Aljava", [...armor, { area: "quiver", index: 0 }], [...ARMOR_SLOTS.map((s) => ARMOR_LABELS[s]), "Aljava"]),
      footer,
    );
    const hint = h("p", { class: "hint" }, "Arraste para mover · solte fora para largar · botão direito divide · Shift+clique transfere · duplo clique junta");
    this.element = panel({ title: "Inventário", icon: "icone-inventario", onClose: onClose ?? (() => this.toggle(false)) }, h("div", { class: "inv-corpo" }, categories, center, this.detail.box), hint);
    this.element.id = "inventario";
    this.element.classList.add("inv-painel");
    this.element.hidden = true;
    this.setCategory("todos");
    parent.appendChild(this.element);
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("pointercancel", this.cancelDrag);
    window.addEventListener("blur", this.cancelDrag);
  }

  /** O detalhe do item (a coluna da direita do ui-c): ícone, nome, tipo, nível, selos, dica, atributos e ações. */
  private buildDetail(): DetailView {
    const itemIcon = h("div", { class: "inv-detalhe-icone", "aria-hidden": "true" });
    const name = h("div", { class: "inv-detalhe-nome" });
    const type = h("div", { class: "inv-detalhe-tipo" });
    const badges = h("div", { class: "inv-detalhe-selos" });
    const hintText = h("p", { class: "inv-detalhe-dica" });
    const stats = h("div", { class: "inv-detalhe-atributos" });
    const buttons = new Map<SlotActionId, HTMLButtonElement>();
    const actions = h(
      "div",
      { class: "inv-acoes" },
      ...slotActions({ area: "inv", index: 0 }, EMPTY, 0, "nenhum").map(({ id, icon: art }) => {
        // Os botões são fixos e atualizados no lugar (recriar sob o ponteiro perderia o clique).
        const b = button({ label: "", icon: art, variant: "acao", onClick: () => this.act(id) });
        b.dataset["acao"] = id;
        b.title = ACTION_SHORTCUTS[id];
        buttons.set(id, b);
        return b;
      }),
    );
    const box = h(
      "aside",
      { class: "inv-detalhe", "aria-label": "Detalhe do item" },
      h("div", { class: "inv-detalhe-topo" }, itemIcon, h("div", { class: "inv-detalhe-titulo" }, name, type, badges)),
      divider(),
      hintText,
      stats,
      actions,
    );
    return { box, icon: itemIcon, name, type, badges, hint: hintText, stats, buttons };
  }

  /**
   * Mostra a estação aberta (ou esconde, com `undefined`): rótulos, slots usados, barras de fogo e
   * de progresso (extrapoladas entre os eventos) e o livro de receitas descobertas. Baú: só os 18
   * slots, com o nome dele (`title`).
   */
  updateStation(st: (StationLike & { slots: Iterable<ItemStackType> }) | undefined, known: (itemId: string) => boolean, now: number, id = "", title = ""): void {
    if (!st) {
      if (!this.stationBox.hidden) {
        this.stationBox.hidden = true;
        // Fechou (revisão da F82): nada da estação fica no detalhe nem recebe ação.
        if (this.hovered?.area === "estacao") this.hovered = undefined;
        if (this.inspected?.area === "estacao") this.inspect(undefined);
        for (const key of [...this.stacks.keys()]) if (key.startsWith("estacao:")) this.stacks.delete(key);
        this.renderDetail();
      }
      this.stationKey = "";
      this.stationOpenedAt = -1;
      return;
    }
    // Reabrir (ou abrir outra) reancora as barras no instantâneo que o servidor manda ao abrir.
    if (this.stationOpenedAt < 0) this.stationOpenedAt = now;
    const kind = st.kind as ProcessStation;
    const chest = st.kind === CHEST_KIND;
    this.stationBox.hidden = false;
    const slots = [...st.slots];
    const labels = chest ? slots.map(() => "") : stationLabels(kind);
    // O livro entra na chave: receitas descobertas com o painel aberto aparecem na hora.
    const book = chest ? [] : stationBook(kind, known, st.recipe);
    const key = `${id}|${kind}|${st.recipe}|` + slots.map((s) => `${s.itemId}:${s.qty}`).join("|") + "#" + book.map((l) => l.id).join(",");
    if (key !== this.stationKey) {
      this.stationKey = key;
      this.stationTitle.textContent = chest ? title || "Baú" : stationTitle(kind);
      this.stationBox.classList.toggle("bau", chest);
      for (const b of this.bars) b.hidden = chest;
      this.book.hidden = chest;
      for (let i = 0; i < STATION_MAX_SLOTS; i++) {
        const el = this.slots.get(refKey({ area: "estacao", index: i }));
        if (!el) continue;
        el.hidden = i >= labels.length;
        if (i < labels.length && labels[i]) el.dataset["label"] = labels[i] ?? "";
        else delete el.dataset["label"];
        const s = slots[i];
        if (s) this.paint({ area: "estacao", index: i }, s, false);
      }
      this.book.replaceChildren(...book.map((line) => h("li", { class: line.current ? "atual" : undefined }, line.text)));
      this.renderDetail();
    }
    if (chest) return;
    const bars = this.stationClock.bars(st, now, `${id}@${this.stationOpenedAt}`);
    this.fireFill.style.transform = `scaleX(${bars.fire.toFixed(3)})`;
    this.progressFill.style.transform = `scaleX(${bars.progress.toFixed(3)})`;
  }

  get open(): boolean {
    return !this.element.hidden;
  }

  toggle(force?: boolean): void {
    this.element.hidden = !(force ?? this.element.hidden);
    if (this.open) this.lastKey = "";
    // Fechar no meio de um arraste cancela: nunca vira descarte acidental.
    else {
      this.cancelDrag();
      this.hovered = undefined;
    }
  }

  private readonly cancelDrag = () => {
    this.drag?.ghost.remove();
    this.drag = undefined;
  };

  update(inventory: InventoryType | undefined): void {
    if (!inventory) return;
    const key = [...inventory.slots, ...inventory.armor, inventory.quiver].map((s) => `${s.itemId}:${s.qty}`).join("|") + `#${inventory.selected}#${inventory.coins}`;
    if (key === this.lastKey) return;
    this.lastKey = key;
    this.held = inventory.selected;
    inventory.slots.forEach((s, i) => this.paint({ area: "inv", index: i }, s, i === inventory.selected));
    inventory.armor.forEach((s, i) => this.paint({ area: "armor", index: i }, s, false));
    this.paint({ area: "quiver", index: 0 }, inventory.quiver, false);
    const { used, total } = capacity([...inventory.slots]);
    setBar(this.itemsBar, used, total);
    this.coins.textContent = String(inventory.coins);
    this.renderDetail();
  }

  /** Escolhe o slot do detalhe (clique, foco ou `undefined` para voltar ao item na mão). */
  inspect(ref: SlotRef | undefined): void {
    if (this.inspected) this.slots.get(refKey(this.inspected))?.classList.remove("inspecionado");
    this.inspected = ref;
    if (ref) this.slots.get(refKey(ref))?.classList.add("inspecionado");
    this.renderDetail();
  }

  private setCategory(category: CraftCategory): void {
    this.category = category;
    for (const [id, b] of this.categoryButtons) {
      b.classList.toggle("ativa", id === category);
      b.setAttribute("aria-pressed", String(id === category));
    }
    for (const [key, el] of this.slots) this.applyFilter(el, this.stacks.get(key)?.itemId ?? "");
  }

  /** Fora da categoria, o slot cheio fica apagado (continua lá: arrastar e soltar seguem valendo). */
  private applyFilter(el: HTMLElement, itemId: string): void {
    el.classList.toggle("fora", itemId !== "" && !inCategory(this.category, itemId));
  }

  private storage(): OpenStorage {
    if (this.stationBox.hidden) return "nenhum";
    return this.stationBox.classList.contains("bau") ? "bau" : "estacao";
  }

  /** O slot do detalhe: com o foco nas ações, o fixado (o teclado age nele); senão o do ponteiro, o fixado ou o da mão. */
  private shownRef(): SlotRef {
    if (this.inspected && this.detail.box.contains(document.activeElement)) return this.inspected;
    return this.hovered ?? this.inspected ?? { area: "inv", index: this.held };
  }

  /** O detalhe do slot mostrado; só mexe no DOM quando o item, a quantidade ou o contexto mudam. */
  private renderDetail(): void {
    const ref = this.shownRef();
    const stack = this.stacks.get(refKey(ref)) ?? EMPTY;
    const storage = this.storage();
    const key = `${refKey(ref)}|${stack.itemId}:${stack.qty}|${this.held}|${storage}`;
    if (key === this.detailKey) return;
    this.detailKey = key;
    const d = itemDetail(stack.itemId, stack.qty);
    const v = this.detail;
    v.box.classList.toggle("vazio", !d);
    v.icon.style.backgroundImage = d ? `url(${this.icons.url(d.id)})` : "";
    v.name.textContent = d?.name ?? slotName(ref);
    v.type.textContent = d?.type ?? "Vazio";
    v.badges.replaceChildren(...(d ? [pips(d.tier, MAX_TIER), ...d.badges.map((b) => badge(b))] : []));
    v.hint.textContent = d?.hint ?? "Passe o mouse num item para ver os detalhes; clique para fixar.";
    v.stats.replaceChildren(...(d?.stats ?? []).map((s) => statRow(s.icon, s.label, s.value)));
    for (const a of slotActions(ref, stack, this.held, storage)) {
      const b = v.buttons.get(a.id);
      if (!b) continue;
      b.disabled = !a.enabled;
      const label = b.querySelector(".k-rotulo");
      if (label) label.textContent = a.label;
    }
  }

  /** Botão do detalhe: a intenção correspondente, no slot mostrado (o servidor valida). */
  private act(id: SlotActionId): void {
    const ref = this.shownRef();
    const stack = this.stacks.get(refKey(ref)) ?? EMPTY;
    if (!stack.itemId) return;
    if (id === "transferir") this.actions.quick(ref);
    else if (id === "dividir") this.actions.split(ref);
    else if (id === "segurar" && ref.area === "inv") this.actions.select(ref.index);
    else if (id === "largar") this.actions.discard(ref);
  }

  private slotElement(ref: SlotRef, label?: string): HTMLElement {
    const el = h("div", { class: "slot", "data-ref": refKey(ref), tabindex: 0, role: "button", "aria-label": slotName(ref) }, h("span", { class: "qty" }));
    if (label) el.dataset["label"] = label;
    if (ref.area === "inv" && ref.index < BELT_SLOTS) el.dataset["key"] = String(ref.index + 1);
    el.addEventListener("pointerdown", (e) => this.onPointerDown(e, ref));
    el.addEventListener("pointerenter", () => {
      this.hovered = ref;
      this.renderDetail();
    });
    el.addEventListener("pointerleave", () => {
      this.hovered = undefined;
      this.renderDetail();
    });
    // Teclado (revisão da F82): Enter ou Espaço fixam o slot e levam às ações; o foco só passando não fixa
    // (o Tab até os botões passaria por todos os slots e fixaria o último).
    el.addEventListener("keydown", (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      this.inspect(ref);
      [...this.detail.buttons.values()].find((b) => !b.disabled)?.focus();
    });
    el.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      this.actions.split(ref);
    });
    el.addEventListener("dblclick", () => {
      const id = el.dataset["item"];
      if (id) this.actions.gather(id);
    });
    this.slots.set(refKey(ref), el);
    return el;
  }

  private paint(ref: SlotRef, stack: ItemStackType, selected: boolean): void {
    const el = this.slots.get(refKey(ref));
    if (!el) return;
    const def = itemDef(stack.itemId);
    this.stacks.set(refKey(ref), { itemId: stack.itemId, qty: stack.qty });
    el.dataset["item"] = stack.itemId;
    el.style.backgroundImage = stack.itemId ? `url(${this.icons.url(stack.itemId)})` : "";
    el.title = describeItem(def);
    el.setAttribute("aria-label", def ? `${slotName(ref)}: ${def.name}${stack.qty > 1 ? ` ×${stack.qty}` : ""}` : slotName(ref));
    // Tier sem cor (F60): ◆ por nível, no canto do slot.
    if (def && def.tier > 0) el.dataset["pips"] = pipsText(def.tier);
    else delete el.dataset["pips"];
    el.classList.toggle("selected", selected);
    el.classList.toggle("filled", Boolean(stack.itemId));
    this.applyFilter(el, stack.itemId);
    (el.firstChild as HTMLElement).textContent = stack.qty > 1 ? String(stack.qty) : "";
  }

  private readonly onPointerDown = (e: PointerEvent, ref: SlotRef) => {
    if (e.button !== 0) return;
    const el = this.slots.get(refKey(ref));
    if (!el?.dataset["item"]) return;
    if (e.shiftKey) {
      this.actions.quick(ref);
      return;
    }
    const ghost = el.cloneNode(true) as HTMLElement;
    ghost.classList.add("ghost");
    ghost.removeAttribute("data-ref");
    document.body.appendChild(ghost);
    this.drag = { from: ref, ghost };
    this.moveGhost(e);
  };

  private readonly onPointerMove = (e: PointerEvent) => {
    if (this.drag) this.moveGhost(e);
  };

  private readonly onPointerUp = (e: PointerEvent) => {
    const drag = this.drag;
    if (!drag) return;
    this.drag = undefined;
    drag.ghost.remove();
    const target = document.elementFromPoint(e.clientX, e.clientY)?.closest<HTMLElement>(".slot");
    const key = target?.dataset["ref"];
    if (key && key !== refKey(drag.from)) {
      const [area, index] = key.split(":");
      const to = { area: area as SlotRef["area"], index: Number(index) };
      this.actions.move(drag.from, to);
      // O detalhe segue a pilha para onde ela foi.
      this.inspect(to);
      return;
    }
    // Soltou fora do painel (ainda aberto): larga no chão.
    if (this.open && !target && !this.element.contains(document.elementFromPoint(e.clientX, e.clientY))) this.actions.discard(drag.from);
  };

  private moveGhost(e: PointerEvent): void {
    if (!this.drag) return;
    this.drag.ghost.style.left = `${e.clientX - 24}px`;
    this.drag.ghost.style.top = `${e.clientY - 24}px`;
  }

  remove(): void {
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("pointercancel", this.cancelDrag);
    window.removeEventListener("blur", this.cancelDrag);
    this.cancelDrag();
    this.element.remove();
  }
}

/** Cinto sempre visível na base da tela, com o slot selecionado destacado. */
export class BeltHud {
  private readonly element: HTMLElement;
  private readonly cells: HTMLElement[] = [];
  private readonly icons: ItemIcons;
  /** Bolsa de tostões (fora dos slots, doc 07, I1). */
  private readonly purse: HTMLElement;
  private lastKey = "";
  private lastCoins = -1;

  constructor(parent: HTMLElement, icons: ItemIcons) {
    this.icons = icons;
    this.element = document.createElement("div");
    this.element.id = "cinto";
    this.element.setAttribute("aria-label", "Cinto");
    this.purse = document.createElement("div");
    this.purse.id = "bolsa";
    this.purse.setAttribute("aria-label", "Moedas");
    this.purse.hidden = true;
    for (let i = 0; i < BELT_SLOTS; i++) {
      const cell = document.createElement("div");
      cell.className = "slot";
      cell.dataset["key"] = String(i + 1);
      const qty = document.createElement("span");
      qty.className = "qty";
      cell.appendChild(qty);
      this.cells.push(cell);
      this.element.appendChild(cell);
    }
    parent.append(this.element, this.purse);
  }

  update(inventory: InventoryType | undefined): void {
    if (!inventory) return;
    if (inventory.coins !== this.lastCoins) {
      this.lastCoins = inventory.coins;
      this.purse.hidden = inventory.coins <= 0;
      this.purse.textContent = `${inventory.coins} ${inventory.coins === 1 ? "moeda" : "moedas"}`;
    }
    const key = inventory.slots.slice(0, BELT_SLOTS).map((s) => `${s.itemId}:${s.qty}`).join("|") + `#${inventory.selected}`;
    if (key === this.lastKey) return;
    this.lastKey = key;
    this.cells.forEach((cell, i) => {
      const stack = inventory.slots[i];
      const id = stack?.itemId ?? "";
      cell.style.backgroundImage = id ? `url(${this.icons.url(id)})` : "";
      cell.title = itemDef(id)?.name ?? "";
      const tier = itemDef(id)?.tier ?? 0;
      if (tier > 0) cell.dataset["pips"] = pipsText(tier);
      else delete cell.dataset["pips"];
      cell.classList.toggle("selected", i === inventory.selected);
      (cell.firstChild as HTMLElement).textContent = stack && stack.qty > 1 ? String(stack.qty) : "";
    });
  }

  remove(): void {
    this.element.remove();
  }
}
